import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/common";
import { MODULE_ICONS } from "@/lib/icons";
import { Button } from "@/components/ui/button";
import { Grid3x3, Download } from "@/lib/icons";

export default function Settings() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/settings").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <div className="text-slate-500">Loading…</div>;

  const mb = (data.storage.total_bytes / (1024 * 1024)).toFixed(2);

  const exportData = async () => {
    const { data: ex } = await api.get("/export");
    const blob = new Blob([JSON.stringify(ex, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `docklinc-export-${Date.now()}.json`; a.click();
    URL.revokeObjectURL(url);
    toast.success("Exported records + media manifest");
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <PageHeader eyebrow="Settings" color="#64748B" title="Settings" subtitle="Account, workspace, modules, storage and export." />

      <Card title="Workspace">
        <Row label="Name" value={data.workspace?.name} />
        <Row label="Your role" value={<span className="capitalize">{data.role}</span>} />
        <Row label="Members" value={data.members.length} />
      </Card>

      <Card title="Storage use">
        <Row label="Media stored" value={`${mb} MB`} />
        <Row label="Files" value={data.storage.asset_count} />
        <Row label="Backend" value={<span className="capitalize">{data.integrations.storage.backend}</span>} />
      </Card>

      <Card title="Integration status">
        <Row label="Database (preview)" value={<span className="capitalize text-emerald-400">{data.integrations.database.backend} · connected</span>} />
        <Row label="Database (production target)" value={<span className="uppercase text-slate-300">{data.integrations.database.production_target}</span>} />
        <Row label="Object storage" value={<span className="text-emerald-400 capitalize">{data.integrations.storage.backend} · connected</span>} />
        <Row label="AI (optional, Stage 3)" value={<span className="text-slate-500">{data.integrations.ai.status.replace("_", " ")}</span>} />
      </Card>

      <Card title="Modules registry">
        <p className="text-xs text-slate-500 mb-3">The six canonical C⁶ functions are listed here in order. Future modules extend the registry after them without renaming those functions.</p>
        <div className="space-y-2">
          {data.modules.map((m) => {
            const Icon = MODULE_ICONS[m.icon] || Grid3x3;
            return (
              <div key={m.key} className="flex items-center gap-3" data-testid={`module-row-${m.key}`}>
                <span className="h-8 w-8 rounded-md grid place-items-center" style={{ background: m.color + "22", color: m.color }}><Icon size={15} /></span>
                <div className="flex-1"><div className="text-sm text-white">{m.label} <span className="text-[10px] text-slate-500 font-mono-label">· {m.key}</span></div><div className="text-[11px] text-slate-500">{m.tagline}</div></div>
                <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: m.enabled ? "#10B98122" : "#64748B22", color: m.enabled ? "#10B981" : "#94A3B8" }}>{m.enabled ? "enabled" : "disabled"}</span>
              </div>
            );
          })}
        </div>
      </Card>

      <Card title="Data export">
        <p className="text-xs text-slate-500 mb-3">Export readable records, IDs, relationships and a media manifest with authorized download links.</p>
        <Button onClick={exportData} className="gap-2" data-testid="export-btn" style={{ background: "#2563EB" }}><Download size={15} /> Export workspace data</Button>
      </Card>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="dl-card p-5">
      <div className="font-mono-label text-[11px] text-slate-500 mb-3">{title}</div>
      {children}
    </div>
  );
}
function Row({ label, value }) {
  return <div className="flex items-center justify-between py-1.5 text-sm"><span className="text-slate-400">{label}</span><span className="text-white">{value}</span></div>;
}
